/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Where the "terug" link at the top of a provider page points.
 *
 * The link used to be a fixed "Terug naar de vergelijking" → the homepage, on
 * all 220 baked provider URLs. For the visitor who clicked through from the
 * aanbiedersoverzicht that is simply false: they were never in the vergelijker,
 * and the breadcrumb directly above the link already says the parent is the
 * overview.
 *
 * So the link follows the visitor: every in-site link to a provider page
 * carries where it was clicked (`providerBackState`, React Router state — the
 * same idiom as `preserveScroll`, and unlike a `?from=` param it cannot mint a
 * second indexable URL for a route that is in the sitemap), and this module is
 * the ONE rule that turns that into a target. Only two origins earn their own
 * answer; everything else — a cold entry from search, an AI answer, a shared
 * link, a kennisbank article — falls back to the overview, the parent in the
 * breadcrumb.
 *
 * Pure on purpose: no React, no i18n. The caller renders `labelKey` through
 * `t()`. `shouldStepBack` (below) is the one rule for when a click on that link
 * steps back in history instead; src/hooks/useBackLinkClick.ts wires it.
 */

import { getCompareAnchor, type CompareLanguage } from './compareAnchor';
import { splitLanguageFromPath } from './gtag';
import { PROVIDERS_SEGMENT, providerHubPath } from './providerRoutes.js';

/** The router-state field carrying the page a provider link was clicked on. */
export interface ProviderBackState {
  providerBackFrom?: string;
}

export interface ProviderBackTarget {
  href: string;
  labelKey: 'provider_profile.back_to_overview' | 'provider_profile.back_to_comparison';
}

/**
 * Attach to every in-site `<Link>` to `/providers/<slug>`:
 * `state={providerBackState(location)}`. Deliberately the whole address
 * (query + hash included) — the comparer keeps its filters in the query
 * string, so this is what makes "terug" return the visitor to the result they
 * were looking at instead of to an unfiltered page.
 */
export const providerBackState = (
  location: Pick<Location, 'pathname' | 'search' | 'hash'>
): ProviderBackState => ({
  providerBackFrom: `${location.pathname}${location.search}${location.hash}`,
});

/** Reads the field back off an unknown router state without trusting it. */
export const providerBackFrom = (state: unknown): string | undefined => {
  const from = (state as ProviderBackState | null | undefined)?.providerBackFrom;
  return typeof from === 'string' ? from : undefined;
};

/**
 * `from` is whatever sat in the history entry — the router state itself or the
 * path already read off it — and never a link target on its own.
 * It must be an in-site absolute path (`//host` and a backslash, which browsers
 * fold into `/`, are other sites wearing a relative-looking coat) AND resolve
 * to one of the two pages that earn their own answer.
 *
 * The language comes from the page, not from `from`: a visitor who switched
 * language on the profile page must go back to the overview in the language
 * they are reading now.
 */
/** `from` taken apart — null when it is not an in-site absolute path. */
interface BackOrigin {
  /** The language prefix the path carried, or null for a path without one. */
  prefix: string | null;
  /** The path without its language prefix (`/` for the homepage). */
  path: string;
  search: string;
  hash: string;
}

const parseBackOrigin = (from: unknown): BackOrigin | null => {
  const raw = typeof from === 'string' ? from : providerBackFrom(from);
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  const hashAt = raw.indexOf('#');
  const hash = hashAt === -1 ? '' : raw.slice(hashAt);
  const beforeHash = hashAt === -1 ? raw : raw.slice(0, hashAt);
  const queryAt = beforeHash.indexOf('?');
  const search = queryAt === -1 ? '' : beforeHash.slice(queryAt);
  const pathname = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
  const prefix = pathname.match(/^\/(en|nl)(?=\/|$)/)?.[1] ?? null;
  return { prefix, path: splitLanguageFromPath(pathname).path, search, hash };
};

/** The overview's path without its language prefix: `/providers`. */
const OVERVIEW_PATH = `/${PROVIDERS_SEGMENT}`;

/** The pages that earn their own answer: the comparer, the two compare tools, the overview. */
const isOwnAnswer = (path: string): boolean =>
  path === '/' || path === '/vergelijk' || path.startsWith('/vergelijk/') || path === OVERVIEW_PATH;

export function resolveProviderBack(from: unknown, language: CompareLanguage): ProviderBackTarget {
  const overview: ProviderBackTarget = {
    href: providerHubPath(language),
    labelKey: 'provider_profile.back_to_overview',
  };
  const origin = parseBackOrigin(from);
  if (!origin) return overview;
  const { path, search, hash } = origin;

  // The comparer lives on the homepage. Its filters are the query string, and
  // its results sit below the hero — so the hash is replaced, not carried:
  // getComparerHref has its own for CTAs (`#start`: Stap 1 from `sm`, the
  // results on a phone), a return link wants the block the visitor came from.
  if (path === '/') {
    return {
      href: `/${language}${search}#${getCompareAnchor(language)}`,
      labelKey: 'provider_profile.back_to_comparison',
    };
  }
  // The head-to-head and multi-compare tools are comparison results too.
  if (path === '/vergelijk' || path.startsWith('/vergelijk/')) {
    return {
      href: `/${language}${path}${search}${hash}`,
      labelKey: 'provider_profile.back_to_comparison',
    };
  }
  // Exactly the overview — `/providers/<slug>` is another provider page.
  if (path === OVERVIEW_PATH) {
    return { href: `/${language}${path}${search}${hash}`, labelKey: overview.labelKey };
  }
  return overview;
}

/** What `shouldStepBack` reads off the click (a React or DOM mouse event fits). */
export interface BackLinkClick {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  defaultPrevented?: boolean;
}

/**
 * Whether a click on the "terug" link should step BACK in history instead of
 * following its href (2026-10-03). The href opens a NEW entry: the comparer
 * fresh, filters only, at the top of its results — while the entry the
 * visitor came from still holds their open rows, "Toon meer", the sort and
 * the scroll position (src/lib/viewState.ts). Stepping back lands on exactly
 * that entry, as the browser's own Back does, and the history does not grow.
 *
 * Only when that entry is certainly the origin: `from` came from router state
 * (only an in-site click sets it, and the entry before a state-carrying entry
 * is the page the click was made on) and resolves to one of the pages that earn
 * their own answer above (never the fallback overview); its language prefix is
 * the page's current one (a visitor who switched language goes forward to the
 * page in the language they read now); there IS an entry before this one in
 * the tab (`historyIdx`, React Router's `history.state.idx`); and it is a plain
 * left click — a modified or middle click opens the href elsewhere, as always.
 * The href stays what `resolveProviderBack` says, for the baked HTML, crawlers
 * and "open in new tab".
 */
export function shouldStepBack(
  from: unknown,
  language: CompareLanguage,
  historyIdx: unknown,
  click: BackLinkClick
): boolean {
  if (click.defaultPrevented) return false;
  if (click.button !== 0 || click.metaKey || click.ctrlKey || click.shiftKey || click.altKey) {
    return false;
  }
  if (typeof historyIdx !== 'number' || !(historyIdx > 0)) return false;
  const origin = parseBackOrigin(from);
  return !!origin && origin.prefix === language && isOwnAnswer(origin.path);
}
